import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import AddProject from './components/AddProject';
import LoadingScreen from './components/LoadingScreen';
import GetCase from './components/GetCase';
import CallbackOutlookApiPage from './components/CallbackOutlookApi';
import GlobalSetting from './components/GlobalSetting';
import LoginPage from './components/LoginPage';
import AuthRoute from './components/AuthRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ReadOnlyProjectDashboard from './components/ReadOnlyProjectDashboard';
import ValidatorWorkspace from './components/ValidatorWorkspace';
import { DashboardProvider } from './components/Dashboard/DashboardContext';
import { Box } from '@mui/material';
import { useWebSocket } from './components/WebSocketContext';
import { useUser } from './components/WebSocketContext';
import DisconnectedOverlay from './components/DisconnectOverlay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import StatusBar from './components/StatusBar';
import SoftwareCanBeUpdated from './components/SoftwareCanBeUpdated';
import MfaSetupDialog from './components/MfaSetupDialog';
import PurchaseCredits from './components/PurchaseCredits';
import ConnectorCreatorProvider from './components/ConnectorCreatorProvider';
import { useTranslation } from 'react-i18next';
import { subscribeToCaseCreationState } from './Utils/caseCreationState';

function HomeRoute() {
  const { permissions, isReadOnly, isValidator } = useUser();

  if (!permissions) {
    return null;
  }

  if (isValidator) {
    return <Navigate to="/validate" replace />;
  }

  if (isReadOnly) {
    return <Navigate to="/project-dashboard" replace />;
  }

  return (
    <ProtectedRoute>
      <AddProject />
    </ProtectedRoute>
  );
}

function RouteAccessModeSync() {
  const location = useLocation();
  const { setPublicRouteActive } = useWebSocket();

  useEffect(() => {
    setPublicRouteActive(location.pathname.startsWith('/public/case/'));
  }, [location.pathname, setPublicRouteActive]);

  return null;
}

function ValidatorCaseRoute() {
  const { isValidator } = useUser();
  const { caseid } = useParams();
  return isValidator ? <Navigate to={`/validate/${caseid}`} replace /> : <Dashboard />;
}

function ValidatorProjectDashboardRoute() {
  const { isValidator } = useUser();
  return isValidator ? <Navigate to="/validate" replace /> : <ReadOnlyProjectDashboard />;
}

function SidebarForRoute() {
  return <Sidebar />;
}

function ValidatorWorkspaceRoute() {
  return (
    <DashboardProvider>
      <ValidatorWorkspace />
    </DashboardProvider>
  );
}

function PurchaseCreditsRoute() {
  const { isConnected, sendMessage, subscribeToMessages } = useWebSocket();
  const [isPaymentsEnabled, setIsPaymentsEnabled] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage.command !== 'configuration_data') {
        return;
      }

      const payload = parsedMessage.data || {};
      if (payload.paymentsEnabled !== undefined || payload.tokenPurchasable !== undefined) {
        setIsPaymentsEnabled(Boolean(payload.paymentsEnabled ?? payload.tokenPurchasable));
      }
    });

    return () => unsubscribe();
  }, [subscribeToMessages]);

  useEffect(() => {
    if (!isConnected || typeof sendMessage !== 'function') {
      return;
    }
    sendMessage({ command: 'get_performance_settings' });
  }, [isConnected, sendMessage]);

  if (isPaymentsEnabled === false) {
    return <Navigate to="/Settings" replace />;
  }

  if (isPaymentsEnabled === null) {
    return null;
  }

  return <PurchaseCredits />;
}

function CaseCreationOverlay() {
  const location = useLocation();
  const { t } = useTranslation();
  const [isCreatingProject, setIsCreatingProject] = React.useState(false);

  useEffect(() => subscribeToCaseCreationState(setIsCreatingProject), []);

  const pathname = String(location.pathname || '');
  const hideOverlay =
    pathname.startsWith('/case/') ||
    pathname.startsWith('/public/case/') ||
    pathname === '/login';

  if (!isCreatingProject || hideOverlay) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
      }}
    >
      <LoadingScreen
        status="updating"
        updateMessage={t('addcase.creating_project', 'Creating your project...')}
      />
    </Box>
  );
}

function App() {
  const { isConnected } = useWebSocket();

  const animationStyle = `
    @keyframes scaleAnimation {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.2);
      }
    }
  `;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ConnectorCreatorProvider>
        <Router>
          <RouteAccessModeSync />
          <StatusBar />
          <SoftwareCanBeUpdated />
          <MfaSetupDialog />
          <CaseCreationOverlay />

          <Routes>
            {/* Login route - accessible without authentication */}
            <Route path="/login" element={<LoginPage />} />

            {/* Public case route - accessible without authentication */}
            <Route path="/public/case/:caseid" element={
              <div style={{ display: 'flex', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
                <DisconnectedOverlay isConnected={isConnected} />
                <Box flexGrow={1} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                  <Dashboard />
                </Box>
              </div>
            } />

            {/* Protected routes */}
            <Route path="/*" element={
              <AuthRoute>
                <div style={{ display: 'flex', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
                  <DisconnectedOverlay isConnected={isConnected} />

                  <SidebarForRoute />
                  <Box flexGrow={1} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                    <style>
                      {animationStyle}
                    </style>
                    <Routes>
                      <Route path="/case/:caseid" element={<ValidatorCaseRoute />} />
                      <Route path="/add" element={
                        <ProtectedRoute>
                          <AddProject />
                        </ProtectedRoute>
                      } />
                      <Route path="/purchase-credits" element={
                        <PurchaseCreditsRoute />
                      } />
                      <Route path="/get" element={<GetCase />} />
                      <Route path="/Settings" element={
                        <ProtectedRoute allowReadOnly>
                          <GlobalSetting />
                        </ProtectedRoute>
                      } />
                      <Route path="/callback" element={<CallbackOutlookApiPage />} />
                      <Route path="/project-dashboard" element={<ValidatorProjectDashboardRoute />} />
                      <Route path="/validate" element={<ValidatorWorkspaceRoute />} />
                      <Route path="/validate/:caseid" element={<ValidatorWorkspaceRoute />} />
                      <Route path="/home-denied" element={<Navigate to="/project-dashboard" replace />} />
                      <Route path="/" element={<HomeRoute />} />
                    </Routes>
                  </Box>
                </div>
              </AuthRoute>
            } />
          </Routes>
        </Router>
      </ConnectorCreatorProvider>
    </LocalizationProvider>
  );
}

export default App;
